import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

function MyAccDropdown({ handleLogout, loggingOut }) {
  const userType = useSelector(
    (state) => state.auth?.user?.user?.user_type || ""
  );
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  // List all routes that should highlight “My Account”
  const accountRoutes = ["/profile", "/orders", "/payments"];
  const isAccountActive = accountRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  return (
    <div>
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            className={`font-semibold flex items-center px-3 py-1 gap-1 rounded-ee-md rounded-3xl transition
              hover:bg-normal-theme-color hover:text-white focus:outline-none focus-visible:outline-none
              ${isAccountActive ? "bg-normal-theme-color text-white" : ""}`}
          >
            <span className="font-semibold">My Account</span>
            <ChevronDown className="w-4 h-4" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          className="z-50 w-54 my-3 rounded-2xl bg-[#fdf5f5] p-4 shadow-xl animate-fade-in"
          align="start"
        >
          {/* Profile Link */}
          <DropdownMenu.Item onSelect={() => setOpen(false)}>
            <NavLink
              to="/profile"
              className="px-2 flex gap-3 py-1 hover:bg-muted rounded"
              onClick={() => setOpen(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M9.99967 11.6666C12.3009 11.6666 14.1663 9.80117 14.1663 7.49998C14.1663 5.19879 12.3009 3.33331 9.99967 3.33331C7.69849 3.33331 5.83301 5.19879 5.83301 7.49998C5.83301 9.80117 7.69849 11.6666 9.99967 11.6666ZM9.99967 11.6666C6.31778 11.6666 3.33301 13.9052 3.33301 16.6666M9.99967 11.6666C13.6816 11.6666 16.6663 13.9052 16.6663 16.6666"
                  stroke="#008083"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <span>Profile</span>
            </NavLink>
          </DropdownMenu.Item>

          {/* Orders Link */}
          <DropdownMenu.Item onSelect={() => setOpen(false)}>
            <NavLink
              to="/orders"
              className="px-2 flex gap-3 py-1 hover:bg-muted rounded"
              onClick={() => setOpen(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[22px] h-[22px] stroke-current text-teal-800"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M15.5833 8.39472L15.5138 7.45767C15.4501 6.59927 14.7083 5.93419 13.8146 5.93419H11.9937M5.5 17.4166H4.45365C3.465 17.4166 2.68398 16.609 2.75442 15.6594L3.36283 7.45767C3.42651 6.59927 4.16831 5.93419 5.06207 5.93419H6.88298M6.88298 5.93419V4.29384C6.88298 2.93493 8.02705 1.83331 9.43833 1.83331C10.8496 1.83331 11.9937 2.93493 11.9937 4.29384V5.93419M6.88298 5.93419H11.9937M15.5833 13.75C15.5833 14.7625 14.7625 15.5833 13.75 15.5833C12.7375 15.5833 11.9167 14.7625 11.9167 13.75M10.0833 19.25H17.4167C18.4292 19.25 19.25 18.4292 19.25 17.4166V12.8333C19.25 11.8208 18.4292 11 17.4167 11H10.0833C9.07081 11 8.25 11.8208 8.25 12.8333V17.4166C8.25 18.4292 9.07081 19.25 10.0833 19.25Z"
                  stroke="#008083"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <span>My Orders</span>
            </NavLink>
          </DropdownMenu.Item>

          {/* Payment History (only for vendors) */}
          {userType === "vendor" && (
            <DropdownMenu.Item onSelect={() => setOpen(false)}>
              <NavLink
                to="/payments"
                className="px-2 flex gap-3 py-1 hover:bg-muted rounded"
                onClick={() => setOpen(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[22px] h-[22px] fill-current text-teal-800"
                  viewBox="0 0 22 22"
                >
                  <mask
                    id="mask0"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="22"
                    height="22"
                  >
                    <rect width="22" height="22" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0)">
                    <path d="M5.5 20.1666C4.73611 20.1666 4.08681 19.8993 3.55208 19.3646C3.01736 18.8298 2.75 18.1805 2.75 17.4166V14.6666H5.5V1.83331L6.875 3.20831L8.25 1.83331L9.625 3.20831L11 1.83331L12.375 3.20831L13.75 1.83331L15.125 3.20831L16.5 1.83331L17.875 3.20831L19.25 1.83331V17.4166C19.25 18.1805 18.9826 18.8298 18.4479 19.3646C17.9132 19.8993 17.2639 20.1666 16.5 20.1666H5.5ZM16.5 18.3333C16.7597 18.3333 16.9774 18.2455 17.1531 18.0698C17.3288 17.8941 17.4167 17.6764 17.4167 17.4166V4.58331H7.33333V14.6666H15.5833V17.4166C15.5833 17.6764 15.6712 17.8941 15.8469 18.0698C16.0226 18.2455 16.2403 18.3333 16.5 18.3333ZM8.25 8.24998V6.41665H13.75V8.24998H8.25ZM8.25 11V9.16665H13.75V11H8.25ZM15.5833 8.24998C15.3236 8.24998 15.1059 8.16213 14.9302 7.98644C14.7545 7.81074 14.6667 7.59304 14.6667 7.33331C14.6667 7.07359 14.7545 6.85588 14.9302 6.68019C15.1059 6.50449 15.3236 6.41665 15.5833 6.41665C15.8431 6.41665 16.0608 6.50449 16.2365 6.68019C16.4122 6.85588 16.5 7.07359 16.5 7.33331C16.5 7.59304 16.4122 7.81074 16.2365 7.98644C16.0608 8.16213 15.8431 8.24998 15.5833 8.24998ZM15.5833 11C15.3236 11 15.1059 10.9121 14.9302 10.7364C14.7545 10.5607 14.6667 10.343 14.6667 10.0833C14.6667 9.82359 14.7545 9.60588 14.9302 9.43019C15.1059 9.25449 15.3236 9.16665 15.5833 9.16665C15.8431 9.16665 16.0608 9.25449 16.2365 9.43019C16.4122 9.60588 16.5 9.82359 16.5 10.0833C16.5 10.343 16.4122 10.5607 16.2365 10.7364C16.0608 10.9121 15.8431 11 15.5833 11ZM5.5 18.3333H13.75V16.5H4.58333V17.4166C4.58333 17.6764 4.67118 17.8941 4.84688 18.0698C5.02257 18.2455 5.24028 18.3333 5.5 18.3333Z" />
                  </g>
                </svg>
                <span>Payment History</span>
              </NavLink>
            </DropdownMenu.Item>
          )}

          {/* Logout Button */}
          <DropdownMenu.Item
            asChild
            className="focus:outline-none focus-visible:outline-none"
          >
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className={`w-full text-left flex gap-3 px-2 py-1 rounded ${
                loggingOut ? "opacity-50 cursor-not-allowed" : "hover:bg-muted"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-[22px] h-[22px] stroke-current text-teal-800"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M14.6667 15.5833L19.25 11M19.25 11L14.6667 6.41667M19.25 11H8.25M8.25 2.75H7.15C5.60986 2.75 4.83978 2.75 4.25153 3.04973C3.73408 3.31338 3.31338 3.73408 3.04973 4.25153C2.75 4.83978 2.75 5.60986 2.75 7.15V14.85C2.75 16.3901 2.75 17.1602 3.04973 17.7485C3.31338 18.2659 3.73408 18.6866 4.25153 18.9503C4.83978 19.25 5.60986 19.25 7.15 19.25H8.25"
                  stroke="#008083"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{loggingOut ? "Logging out…" : "Logout"}</span>
            </button>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
}

export default MyAccDropdown;
